import type { PaddingOptions } from 'mapbox-gl';
import { useEffect, useMemo, useRef } from 'react';
import { CTRL_SIZE_MD, SPACE_800 } from '@alltrails/denali/tokens';
import type { PageUI } from '@alltrails/layout/utils/PageUI/pageUI';
import { useAppDispatch } from '../redux';
import { updateTrailResults } from '../redux/reducer';
import convertResults from '../utils/convertResults';
import fitBoundsOnLoad from '../utils/fitBoundsOnLoad';
import { sectionsToTrailResults } from '../utils/pageUITrailResults';
import useFitBoundsOnce from './useFitBoundsOnce';

// The map is framed once per response, so each response gets a stable key to remember it by. RTK Query hands every reader
// of a response the same object, so its identity tells a new response apart from a re-render, the other slot reading the
// same response, or a remount.
const responseKeys = new WeakMap<PageUI, string>();
let responseCount = 0;

const framingDuration = 1000;
const labelMargin = SPACE_800;
const controlsSize = CTRL_SIZE_MD;

const getFramingPadding = ({ top = 0, right = 0, bottom = 0, left = 0 }: PaddingOptions): PaddingOptions => ({
  top: top + labelMargin,
  right: right + controlsSize + labelMargin,
  bottom: bottom + controlsSize + labelMargin,
  left: left + labelMargin
});

const getResponseKey = (page: PageUI) => {
  let key = responseKeys.get(page);

  if (!key) {
    responseCount += 1;
    key = `response-${responseCount}`;
    responseKeys.set(page, key);
  }

  return key;
};

/**
 * Puts a PageUI response on the map: pins in the order the server sent them, framed once per response.
 * Pass `undefined` while the response is loading; the map is left as it is until there is one.
 *
 * Coming back to a response it has shown before (ex: scrolling back to an earlier answer in a chat) re-frames only if none of
 * its pins is on screen, so reading back through answers about the same area doesn't keep moving the map.
 */
const usePageUITrailResults = (page: PageUI | undefined) => {
  const dispatch = useAppDispatch();
  const results = useMemo(() => (page ? sectionsToTrailResults(page.sections) : []), [page]);
  const framedPages = useRef(new WeakSet<PageUI>());

  useEffect(() => {
    if (!page) {
      return;
    }

    dispatch(updateTrailResults(results));
  }, [dispatch, page, results]);

  // Frames to the trailheads only, so a hovered trail's polyline appearing later never moves the map. With no results there
  // is nothing to frame to, so the map stays where it is. The extra room is for this fit only.
  useFitBoundsOnce('pageUITrailResults', page && results.length ? getResponseKey(page) : undefined, map => {
    if (!page) {
      return;
    }

    if (framedPages.current.has(page)) {
      const bounds = map.getBounds();

      if (bounds && results.some(({ _geoloc }) => _geoloc && bounds.contains([_geoloc.lng, _geoloc.lat]))) {
        return;
      }
    }

    framedPages.current.add(page);
    fitBoundsOnLoad(
      convertResults(results),
      map,
      framingDuration,
      getFramingPadding(map.getPadding()),
      12,
      false /* linear */,
      false /* retainPadding */
    );
  });
};

export default usePageUITrailResults;
